// SphericalColorPaletteExplorer.tsx
// Main application component orchestrating color palette transformation with 3D visualization

import React, { useState, useCallback } from 'react';
import * as THREE from 'three';
import { PaletteStructure } from './types';
import { DEFAULT_PALETTE_STRUCTURE } from './constants';
import { transformPalette } from './paletteTransform';
import { ThreeScene } from './ThreeScene';
import { ColorInput } from './ColorInput';
import { ColorOutput } from './ColorOutput';
import { VectorDisplay } from './VectorDisplay';
import { PalettePreview } from './PalettePreview';
import { HowItWorksModal } from './HowItWorksModal';
import { HolographicSwatch } from './HolographicSwatch';

const SphericalColorPaletteExplorer: React.FC = () => {
  const [inputStructure, setInputStructure] = useState<PaletteStructure>(DEFAULT_PALETTE_STRUCTURE);
  const [transformedStructure, setTransformedStructure] = useState<PaletteStructure>(DEFAULT_PALETTE_STRUCTURE);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showInput, setShowInput] = useState(true);
  const [currentVector, setCurrentVector] = useState({ x: 0, y: 0, z: 1 });
  const [showEditModal, setShowEditModal] = useState(false);
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  const handlePaletteChange = (structure: PaletteStructure) => {
    setInputStructure(structure);
  };

  const handleInitialize = () => {
    setIsInitialized(true);
    setShowInput(false);
  };

  const handlePointMove = useCallback((position: THREE.Vector3) => {
    setCurrentVector({ x: position.x, y: position.y, z: position.z });
    setInputStructure(currentInputStructure => {
      const newColors = transformPalette(currentInputStructure.colors, position);
      setTransformedStructure({
        colors: newColors,
        groups: currentInputStructure.groups
      });
      return currentInputStructure;
    });
  }, []);

  const handleEditInput = () => {
    setShowEditModal(true);
  };

  const handleEditOriginal = () => {
    // Edit the original input palette
    setShowInput(true);
    setIsInitialized(false);
    setShowEditModal(false);
  };

  const handleEditTransformed = () => {
    // Use the current transformed palette as the new input
    setInputStructure(transformedStructure);
    setShowInput(true);
    setIsInitialized(false);
    setShowEditModal(false);
  };

  // Get the primary color safely
  const getPrimaryColor = () => {
    if (Array.isArray(transformedStructure.colors) && transformedStructure.colors.length > 0) {
      return transformedStructure.colors[0];
    }
    if (Array.isArray(inputStructure.colors) && inputStructure.colors.length > 0) {
      return inputStructure.colors[0];
    }
    return '#8b5cf6';
  };

  return (
    <div className="w-screen h-screen bg-gray-900 text-white flex relative">
      {/* Left Panel - Input/Output */}
      <div className="w-96 h-full overflow-y-auto bg-gray-800 p-4">
        {showInput ? (
          <ColorInput
            paletteStructure={inputStructure}
            onPaletteChange={handlePaletteChange}
            onInitialize={handleInitialize}
          />
        ) : (
          <ColorOutput
            paletteStructure={transformedStructure}
            onEditInput={handleEditInput}
          />
        )}
      </div>

      {/* Right Panel - 3D Sphere */}
      <div className="flex-1 h-full relative">
        {isInitialized ? (
          <>
            <ThreeScene 
              onPointMove={handlePointMove} 
              isInitialized={isInitialized} 
            />
            
            <div className="absolute top-4 right-4 space-y-2">
              {/* How to Use - Collapsible */}
              <div className="bg-gray-800 bg-opacity-90 rounded-lg max-w-xs overflow-hidden">
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-700 transition-colors"
                  onClick={() => setInstructionsExpanded(!instructionsExpanded)}
                >
                  <h3 className="text-lg font-semibold">How to Use</h3>
                  <button className="text-gray-400 hover:text-white text-xl">
                    {instructionsExpanded ? '−' : '+'}
                  </button>
                </div>
                
                {instructionsExpanded && (
                  <div className="px-4 pb-4">
                    <ul className="text-sm text-gray-300 space-y-2">
                      <li><strong>Drag the sphere</strong> to rotate it in any direction</li>
                      <li>The <span className="text-pink-400">pink point</span> stays fixed as your reference</li>
                      <li>As you rotate, colors transform smoothly in perceptually-uniform LAB color space</li>
                      <li>Rotate 180° for inverse colors, or explore any angle for variations</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* How It Works Button */}
              <button
                onClick={() => setShowHowItWorks(true)}
                className="w-full bg-gray-800 bg-opacity-90 hover:bg-gray-700 p-4 rounded-lg transition-colors text-left"
              >
                <h3 className="text-lg font-semibold">How It Works 🔬</h3>
                <p className="text-xs text-gray-400 mt-1">See the math and visualization</p>
              </button>
            </div>

            {/* Holographic Swatch - Primary Color Display */}
            <div className="absolute top-4 left-4 z-0">
              <div className="bg-gray-800 bg-opacity-90 rounded-lg p-3 backdrop-blur-sm shadow-2xl">
                <div className="text-xs text-gray-400 mb-2 font-semibold">Primary Color</div>
                <HolographicSwatch 
                  color={getPrimaryColor()} 
                  className="w-24 h-24 rounded-lg"
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-xs font-mono text-white drop-shadow-lg">
                      {getPrimaryColor()}
                    </span>
                  </div>
                </HolographicSwatch>
              </div>
            </div>

            <PalettePreview palette={transformedStructure} />
            <VectorDisplay vector={currentVector} />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">
                Spherical Color Palette Explorer
              </h2>
              <p className="text-gray-400">
                Create your custom color palette and click "Initialize Sphere" to begin
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Edit Choice Modal - Full Screen Overlay */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full border border-gray-700 shadow-2xl">
            <h3 className="text-xl font-bold mb-4">Edit Palette</h3>
            <p className="text-sm text-gray-400 mb-6">
              Which palette would you like to edit?
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleEditTransformed}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition-colors text-left px-4"
              >
                <div className="font-semibold">Current Transformed Palette</div>
                <div className="text-xs text-blue-200 mt-1">
                  Continue from current sphere position
                </div>
              </button>
              
              <button
                onClick={handleEditOriginal}
                className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-colors text-left px-4"
              >
                <div className="font-semibold">Original Input Palette</div>
                <div className="text-xs text-gray-400 mt-1">
                  Start over with initial values
                </div>
              </button>

              <button
                onClick={() => setShowEditModal(false)}
                className="w-full py-2 text-gray-400 hover:text-gray-300 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Modal */}
      {showHowItWorks && (
        <HowItWorksModal onClose={() => setShowHowItWorks(false)} />
      )}
    </div>
  );
};

export default SphericalColorPaletteExplorer;